import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('vzy_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('vzy_token');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// API methods
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
  register: (data) => api.post('/auth/register', data),
  listUsers: () => api.get('/auth/users'),
  updateUser: (id, data) => api.patch(`/auth/users/${id}`, data),
  toggleUserStatus: (id) => api.patch(`/auth/users/${id}/toggle-status`),
};

export const managementApi = {
  getSummary: (version) => api.get(`/management/summary${version ? `?version=${version}` : ''}`),
  getTrend: (days = 30) => api.get(`/management/trend?days=${days}`),
  getLaunchReadiness: () => api.get('/management/launch-readiness'),
  getAlerts: (days = 7, { version, platform } = {}) => {
    const params = new URLSearchParams({ days });
    if (version) params.append('version', version);
    if (platform && platform !== 'all') params.append('platform', platform);
    return api.get(`/management/alerts?${params}`);
  },
  acknowledgeAlert: (id) => api.post(`/management/alerts/${id}/acknowledge`),
  getPlatformSummary: () => api.get('/management/platform-summary'),
  getPlatformTrend: (days = 30, platform) =>
    api.get(`/management/platform-trend?days=${days}${platform ? `&platform=${platform}` : ''}`),
  getHealthReport: () => api.get('/management/health-report'),
  getTopRootCauses: (limit = 5, { version, platform } = {}) => {
    const params = new URLSearchParams({ limit });
    if (version) params.append('version', version);
    if (platform && platform !== 'all') params.append('platform', platform);
    return api.get(`/management/top-root-causes?${params}`);
  },
  getReleaseRisk: (version) =>
    api.get(`/management/release-risk${version ? `?version=${version}` : ''}`),
  getReleaseRiskPlatform: (version, platform) =>
    api.get(`/management/release-risk-platform?${version ? `version=${version}&` : ''}platform=${platform}`),
  getPlatformLatestVersions: () =>
    api.get('/management/platform-latest-versions'),
  getDeploymentDecision: (version) =>
    api.get(`/management/deployment-decision/${version}`),
  getReleaseTimeline: (days = 90) =>
    api.get(`/management/release-timeline?days=${days}`),
};

export const developerApi = {
  getSummary: (version) => api.get(`/developer/summary${version ? `?version=${version}` : ''}`),
  getAiRecommendations: ({ version, platform } = {}) => {
    const params = new URLSearchParams();
    if (version) params.append('version', version);
    if (platform && platform !== 'all') params.append('platform', platform);
    const qs = params.toString();
    return api.get(`/developer/ai-recommendations${qs ? `?${qs}` : ''}`);
  },
  getCrashes: (params) => api.get('/developer/crashes', { params }),
  getCrashHeatmap: () => api.get('/developer/crashes/heatmap'),
  getPerformance: (params) => api.get('/developer/performance', { params }),
  getDevices: (params) => api.get('/developer/devices', { params }),
  getAnr: (params) => api.get('/developer/anr', { params }),
  getBuildBreakdown: (params) => api.get('/developer/build-breakdown', { params }),
  updateCrashStatus: (id, status) => api.patch(`/developer/crashes/${id}/status`, { status }),
  getVersionRisk: (version) => api.get(`/developer/version-risk/${version}`),
  getVersionRiskCompare: (v1, v2) => api.get(`/developer/version-risk-compare?v1=${v1}&v2=${v2}`),
};

export const versionApi = {
  list: (platform) => api.get('/version/list', { params: platform && platform !== 'all' ? { platform } : {} }),
  compare: (v1, v2) => api.get(`/version/compare?v1=${v1}&v2=${v2}`),
  health: (version) => api.get(`/version/${version}/health`),
  liveStatus: (platform) => api.get('/version/live-status', { params: platform && platform !== 'all' ? { platform } : {} }),
  platformVersions: (platform) => api.get('/version/platform-versions', { params: platform && platform !== 'all' ? { platform } : {} }),
  builds: (version) => api.get('/version/builds', { params: version ? { version } : {} }),
};

export const metricsApi = {
  refresh: () => api.post('/metrics/refresh'),
  cancel: (refreshId) => api.post('/metrics/cancel', { refreshId }),
  getStatus: (id) => api.get(`/metrics/refresh-status/${id}`),
  getHistory: () => api.get('/metrics/refresh-history'),
  getSyncStatus: () => api.get('/metrics/sync-status'),
};

export const metricDetailApi = {
  getTrend: (metric, days = 30, period = 'daily') =>
    api.get(`/metrics/detail/${metric}/trend?days=${days}&period=${period}`),
  getBreakdown: (metric) => api.get(`/metrics/detail/${metric}/breakdown`),
  getRecommendations: (metric) => api.get(`/metrics/detail/${metric}/recommendations`),
};

export const alertApi = {
  getList: (params) => api.get('/alerts', { params }),
  getDetail: (id) => api.get(`/alerts/${id}`),
  acknowledge: (id) => api.post(`/alerts/${id}/acknowledge`),
  bulkAcknowledge: (ids) => api.post('/alerts/bulk-acknowledge', { ids }),
};

export const playVitalsApi = {
  getStatus: () => api.get('/play-vitals/status'),
  getLatest: () => api.get('/play-vitals/latest'),
  getComparison: () => api.get('/play-vitals/comparison'),
  sync: (days = 30) => api.post('/play-vitals/sync', { days }),
};

export default api;
